// Provenance: repo:tds-reconciliation-portal:frontend/src/components/AliasGroupDetail.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// run a group covers is shown by its 8-hex tag.
//
// The regex behind a mapping, and what it reaches now. Read-only on purpose:
// correcting a mapping stays retire-and-rebuild, which is reversible and
// leaves every past run's snapshot meaning what it meant. An editable regex
// would silently change what a group means underneath runs that already used it.
import type { AliasGroup } from '../api/types'
import { runTag } from '../format'
import { coversState } from '../workspace/workspaceSession'
import { Modal } from './Modal'

export function patternNames(pattern: string): string[] | null {
  const m = /^\^\(\?:(.*)\)\$$/.exec(pattern ?? '')
  if (!m) return null
  return m[1]
    .split('|')
    .map((x) => x.replace(/\\(.)/g, '$1').trim())
    .filter(Boolean)
}

/** A regex, when a regex is genuinely what is being shown.
 *
 *  `break-all` is not decoration. A pattern is one unbroken token, so a
 *  max-width had nothing to break on and long groups ran clear across the
 *  neighbouring columns. */
function Pattern({ value }: { value: string }) {
  return (
    <code className="block whitespace-normal break-all rounded bg-muted px-1.5 py-1 font-mono text-[10px] text-foreground">
      {value}
    </code>
  )
}

/** The names a group actually joins in the latest run.
 *
 *  Three states, and telling them apart is the point. Names: it is working,
 *  and here is what on. Empty: it matches nothing — a dead or typo'd pattern,
 *  which used to look identical to a working one. Null: no completed run to
 *  check against, so we do not know, which is not the same as nothing.
 */
function Covers({
  names,
  runId,
  sessionRun,
}: {
  names?: string[] | null
  runId?: string | null
  /** the run this session has open; without one there is nothing to report
   *  membership against, and reporting another run's is the defect */
  sessionRun: string | null
}) {
  const state = coversState(names, sessionRun, runId)
  if (state.kind === 'no-session-run') {
    return <p className="text-xs italic text-muted-foreground">no run loaded in this session</p>
  }
  if (state.kind === 'no-run') {
    return <p className="text-xs italic text-muted-foreground">no completed run to check against</p>
  }
  if (state.kind === 'none') {
    return (
      <p className="text-xs text-orange-700">
        matches nothing in run {runTag(state.runId)}
      </p>
    )
  }
  return (
    <div className="space-y-0.5">
      {state.names.map((n) => (
        <p key={n} className="break-words text-xs text-foreground">{n}</p>
      ))}
    </div>
  )
}

/** The pattern, and what it currently reaches. */
export function GroupDetail({
  group,
  sessionRun,
  onClose,
}: {
  group: AliasGroup
  sessionRun: string | null
  onClose: () => void
}) {
  const side = (title: string, pattern: string, names?: string[] | null) => {
    // What the pattern was BUILT from, when this app generated it. Not the
    // same question as what it reaches now, and the two differing is worth
    // seeing: a name the group was made for that no longer appears in the run
    // means the client stopped using that spelling, not that the alias broke.
    const built = patternNames(pattern)
    return (
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <Pattern value={pattern} />
        {built && (
          <p className="break-words text-xs text-muted-foreground">
            built from: {built.join(', ')}
          </p>
        )}
        <Covers names={names} runId={group.covers_run_id} sessionRun={sessionRun} />
      </div>
    )
  }
  return (
    <Modal opened onClose={onClose} title={group.label} wide>
      <div className="space-y-4">
        {side('26AS pattern', group.regex_26as, group.names_26as)}
        {side('Ledger pattern', group.regex_ledger, group.names_books)}
        <p className="text-xs text-muted-foreground">
          Patterns are shown as written and cannot be edited here. To change a
          mapping, retire this group and build a new one — past runs keep the
          snapshot they reconciled with, so what they reported stays true.
        </p>
      </div>
    </Modal>
  )
}
